// external modules
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { useRecoilState } from "recoil";
// internal modules
import { useAppDispatch } from "store/redux/modules";
import { languageLabels } from "libs/i18n";
import { TOTAL_INTRO_STEP } from "utils/@intro/utils_intro";
// state
import { introStepState } from "store/recoil/@intro/introState";
// components
import BottomSheet from "components/@common/BottomSheet/BottomSheet";
import Login from "pages/Auth/components/Login/Login";
import StepIndicator from "./components/@common/StepIndicator/StepIndicator";
import Step1 from "./components/Step1/Step1";
import Step2 from "./components/Step2/Step2";
import Step3 from "./components/Step3/Step3";
import Step4 from "./components/Step4/Step4";
import Step5 from "./components/Step5/Step5";
// styles & types & assets
import type { RootState } from "store/redux/modules";
import { getCheckKoreaLang } from "utils/getCheckKorea";
import { toaster } from "@becon-team/becon-fe-lib/my-toast";
import { useLanguageSelectModal } from "components/(modal)/LanguageSelectModal";
import { LanguageOutline, LanguageOutline2 } from "@becon-team/becon-fe-lib/my-svgs";

export default function Intro() {
  const { t, i18n } = useTranslation();
  const isKorea = getCheckKoreaLang(i18n);

  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const state = location.state as {
    email: string | undefined;
  };
  const [email, setEmail] = useState("");
  const { openModal: openLanguageSelectModal } = useLanguageSelectModal();
  const [isActiveLoginBottomSheet, setActiveLoginBottomSheet] = useState(false);
  const [introStep, setIntroStep] = useRecoilState(introStepState);
  const dispatch = useAppDispatch();
  const { user, error, userSelectorIsLoading } = useSelector((state: RootState) => ({
    user: state.user.users?.[0],
    userSelectorIsLoading: state.loading["user/GET_USERS"],
    error: state.account.error,
  }));

  const currentLang =
    languageLabels.find((lang) => lang[0] === i18n.languages[0])?.[1] ?? i18n.languages[0].toUpperCase();

  // 로그인 실패 시
  useEffect(() => {
    if (error) {
      switch (error.message) {
        case "AUTH_FAILED:CREDENTIAL_MISMATCH":
          toaster.warning(t("비밀번호를 확인해주세요."));
          break;
        default:
          toast.error(t("로그인에 실패했어요."));
          break;
      }
      setActiveLoginBottomSheet(true);
    }
  }, [error]);

  // 로그인 성공 후
  useEffect(() => {
    if (user?.user_id && userSelectorIsLoading === false) {
      toaster.success(t("로그인에 성공하셨습니다"));
      onCloseLoginBottomSheet();
      navigate(`/`, { replace: true });
    }
  }, [user, dispatch, userSelectorIsLoading, t, navigate]);

  const onCloseLoginBottomSheet = () => {
    setActiveLoginBottomSheet(false);
  };

  useEffect(() => {
    if (state && state?.email) {
      setEmail(state.email);
      setActiveLoginBottomSheet(true);
    }
  }, [state]);

  return (
    <div className={"w-full pt-safe bg-primary-800 h-screen-safe"} ref={wrapperRef}>
      <StepIndicator />
      <button
        className="absolute bottom-16ptv right-sp-16 z-10 flex items-center gap-sp-4 rounded-full border border-base-300/30 bg-white/10 px-sp-12 py-sp-6"
        onClick={() => {
          setIntroStep(1);
          openLanguageSelectModal();
        }}
      >
        <LanguageOutline2 className="w-fs-sm stroke-base-300" />
        <span className="text-xs text-base-300">{currentLang}</span>
      </button>

      {introStep === 1 && <Step1 />}
      {isKorea && introStep === 2 && <Step2 />}
      {isKorea && introStep === 3 && <Step3 />}
      {isKorea && introStep === 4 && <Step4 />}
      {isKorea && introStep === 5 && <Step5 />}

      {!isKorea && (
        <div className={"w-full px-20ptv absolute bottom-60ptv"}>
          <button
            className="my-btn-primary-line text-primary w-full font-bold"
            onClick={() => setActiveLoginBottomSheet(true)}
          >
            {t("시작하기")}
          </button>
        </div>
      )}

      {isKorea && introStep === 1 && (
        <div className={"w-full px-20ptv absolute bottom-60ptv"}>
          <button
            className="my-btn-primary-line text-primary w-full font-bold"
            onClick={() => setIntroStep((prev) => prev + 1)}
          >
            {t("다음")} ({introStep}/{TOTAL_INTRO_STEP})
          </button>
        </div>
      )}

      {(introStep === 2 || introStep === 3 || introStep === 4) && (
        <div className={"w-full px-20ptv absolute bottom-60ptv flex gap-x-sp-12"}>
          <button
            className="my-btn-primary-line text-primary w-full font-bold"
            onClick={() => setIntroStep((prev) => prev - 1)}
          >
            {t("이전")}
          </button>
          <button
            className="my-btn-primary-line text-primary w-full font-bold"
            onClick={() => setIntroStep((prev) => prev + 1)}
          >
            {t("다음")} ({introStep}/{TOTAL_INTRO_STEP})
          </button>
        </div>
      )}

      {introStep === 5 && (
        <div className={"w-full px-20ptv absolute bottom-60ptv"}>
          <button
            className="my-btn-primary-line text-primary w-full font-bold"
            onClick={() => setActiveLoginBottomSheet(true)}
          >
            {t("시작하기")}
          </button>
        </div>
      )}

      <BottomSheet bottomSheetOpen={isActiveLoginBottomSheet} onCloseBottomSheet={onCloseLoginBottomSheet}>
        <Login onCloseLoginBottomSheet={onCloseLoginBottomSheet} email={email} />
      </BottomSheet>
    </div>
  );
}
